/**
 * THE one place third-party code on the marketing site is declared.
 *
 * The marketing origin, `https://lifekees.com`, is where Lifekees allows code it
 * did not write: Cloudflare Turnstile on the signup page today, an analytics
 * provider perhaps later. The vault origin, `https://vault.lifekees.com`,
 * allows none, ever (its CSP is `index.html`'s, and no file here touches it).
 *
 * Everything that lets a third party run on the marketing site is derived from
 * this registry, so there is nowhere else to add one by accident:
 *
 *  - each page's `<meta>` Content-Security-Policy (`site/csp.ts`, written into
 *    the built HTML by `vite.site.config.ts`): a provider's origins appear only
 *    on the pages listed in its `pages`;
 *  - the marketing server block's CSP header in `deploy/nginx-lifekees.conf` and
 *    the `thoth-patch.sh` heredoc must equal the union over every page
 *    (`test/deploy-csp.test.ts` fails until they do);
 *  - `site/src/third-party.ts` is the only code that creates a `<script>`
 *    element, it loads only a `script` named here, only on a listed page, and
 *    only after the old app's storage on this origin has been wiped
 *    (`site/src/old-origin/wipe.ts`). No marketing HTML may carry a
 *    third-party `<script>` tag (`test/signup-isolation.test.ts`).
 *
 * Adding analytics later is therefore: one entry here (its script URL, the
 * origins it loads from and talks to, the pages it runs on), the same origins
 * in the nginx header, and a `loadThirdPartyScript('<name>')` call from the
 * page's entry module. Do not add one before the old-origin wipe has been
 * verified on real browsers (docs/SECURITY.md §11, "The old origin").
 *
 * Imported by the browser bundle (`third-party.ts`) and by the Node build
 * (`csp.ts`), so it imports nothing.
 */

/** The marketing site's pages, by name, and the built document each one is. */
export const SITE_PAGES = {
  landing: 'index.html',
  signup: 'signup/index.html',
  /** nginx's `error_page 404` on the marketing origin. Never a provider's page. */
  notFound: '404.html',
} as const;

export type SitePage = keyof typeof SITE_PAGES;

export interface ThirdParty {
  /** The exact script URL `loadThirdPartyScript` appends. https only. */
  script: string;
  /** CSP `script-src` sources. Must include the origin of {@link script}. */
  scriptSrc: readonly string[];
  /** CSP `frame-src` sources (an iframe the script inserts). */
  frameSrc: readonly string[];
  /** CSP `connect-src` sources (beacons, XHR/fetch the script makes). */
  connectSrc: readonly string[];
  /** CSP `img-src` sources (tracking pixels). */
  imgSrc: readonly string[];
  /** The only pages this provider may run on. */
  pages: readonly SitePage[];
}

export const THIRD_PARTIES = {
  /**
   * Cloudflare Turnstile, the signup bot check. Exactly what Cloudflare
   * documents it needs (developers.cloudflare.com/turnstile/reference/
   * content-security-policy/): `api.js` from challenges.cloudflare.com and the
   * widget's iframe from the same origin. `render=explicit` so the widget is
   * created by `site/src/signup.ts` with our `action` and callbacks, not by a
   * class-name scan.
   */
  turnstile: {
    script: 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit',
    scriptSrc: ['https://challenges.cloudflare.com'],
    frameSrc: ['https://challenges.cloudflare.com'],
    connectSrc: [],
    imgSrc: [],
    pages: ['signup'],
  },
} as const satisfies Record<string, ThirdParty>;

export type ThirdPartyName = keyof typeof THIRD_PARTIES;
